import { useEffect, useRef, useState } from "react";
import { Check, Disc3, Play } from "lucide-react";
import { readCover } from "@/lib/music/meta";
import { startDrag } from "@/lib/music/drag";

const cache = new Map();
let queue = Promise.resolve();

export const useCover = (m, album) => {
  const ref = useRef(null);
  const [src, setSrc] = useState(cache.get(album.key));
  useEffect(() => {
    if (cache.has(album.key) || !ref.current) return undefined;
    let live = true;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      queue = queue.then(async () => {
        if (cache.has(album.key)) return;
        let blob = null;
        try { blob = await readCover(await m.file(album.paths[0])); } catch { /* no art */ }
        if (!blob) blob = await m.art(album.paths[0]).catch(() => null);
        const url = blob ? URL.createObjectURL(blob) : null;
        cache.set(album.key, url);
        if (live) setSrc(url);
      }).catch(() => {});
    }, { rootMargin: "300px" });
    io.observe(ref.current);
    return () => { live = false; io.disconnect(); };
  }, [album.key]); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, src];
};

const Tile = ({ m, album, i, selected, onToggle, onOpen, dragPaths }) => {
  const [ref, src] = useCover(m, album);
  return (
    <div ref={ref} draggable onDragStart={(e) => startDrag(e, dragPaths(album), album.name)} className="group cursor-grab animate-in fade-in duration-500" data-testid={`insp-album-${i}`}>
      <div onClick={onOpen} title="Open album" className={`relative aspect-square cursor-pointer overflow-hidden rounded-md border transition-all ${selected ? "border-[#F59E0B] ring-2 ring-[#F59E0B]/60" : "border-[#2D3442] group-hover:border-[#D97706]/50"}`} data-testid={`insp-album-cover-${i}`}>
        {src ? <img src={src} alt={album.name} className="h-full w-full object-cover" draggable={false} /> : <div className="grid h-full w-full place-items-center bg-[#13171F] text-[#3A4252]"><Disc3 className="h-1/3 w-1/3" /></div>}
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} title={selected ? "Unselect album" : "Select album"} aria-label="Select album" className={`absolute left-2 top-2 grid h-6 w-6 place-items-center rounded-full border text-[#0B0D10] transition-opacity ${selected ? "border-[#F59E0B] bg-[#F59E0B] opacity-100" : "border-white/60 bg-black/30 opacity-0 hover:bg-black/60 group-hover:opacity-100"}`} data-testid={`insp-album-select-${i}`}>{selected && <Check className="h-3 w-3" />}</button>
        <button onClick={(e) => { e.stopPropagation(); m.play(album.paths, null, album.name); }} title="Play album" className="absolute bottom-2 right-2 grid h-9 w-9 translate-y-1 place-items-center rounded-full bg-[#D97706] text-[#0B0D10] opacity-0 shadow-lg transition-all group-hover:translate-y-0 group-hover:opacity-100" data-testid={`insp-album-play-${i}`}><Play className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 truncate text-sm text-[#F3EBDD]" title={album.name}>{album.name}</div>
      <div className="truncate text-xs text-[#7A7468]">{[album.artist, album.year, `${album.paths.length} ${album.paths.length === 1 ? "song" : "songs"}`].filter(Boolean).join(" · ")}</div>
    </div>
  );
};

const DISC = /^(disc|disk|cd)\s*\d+$/i;
export const albumDir = (path) => { const d = path.split("/").slice(0, -1); if (d.length > 1 && DISC.test(d[d.length - 1])) d.pop(); return d.join("/"); };

export const groupAlbums = (list) => {
  const map = new Map();
  list.forEach((t) => {
    const dir = albumDir(t.path);
    const name = t.album || dir.split("/").pop() || "Unsorted";
    const key = `${dir}::${name.toLowerCase()}`; // keep in sync with albumKey
    if (!map.has(key)) map.set(key, { key, name, artist: t.albumartist, paths: [], artists: new Set(), years: {} });
    const a = map.get(key);
    a.paths.push(t.path);
    if (t.artist) a.artists.add(t.artist);
    if (t.year) a.years[t.year] = (a.years[t.year] || 0) + 1;
  });
  return [...map.values()].map(({ artists, years, ...a }) => ({ ...a, year: Number(Object.entries(years).sort((x, y) => y[1] - x[1])[0]?.[0]) || undefined, artist: a.artist || (artists.size > 1 ? "Various artists" : [...artists][0]) }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const albumKey = (path, name) => `${albumDir(path)}::${name.toLowerCase()}`;

export const ALBUM_SORTS = [["name", "Name"], ["artist", "Artist"], ["new", "Newest first"], ["old", "Oldest first"]];
export const sortAlbums = (list, by) => {
  if (by === "name") return list;
  const cmp = (a, b) => (by === "artist" ? String(a.artist || "").localeCompare(String(b.artist || "")) : by === "new" ? b.year - a.year : a.year - b.year);
  const missing = (a) => (by === "artist" ? !a.artist : !a.year);
  return [...list].sort((a, b) => (missing(a) - missing(b)) || cmp(a, b) || a.name.localeCompare(b.name));
};

let cacheLib = null;

export const AlbumGrid = ({ m, albums, sel, setSel, onOpen }) => {
  if (cacheLib !== m.dirName) { cache.forEach((u) => u && URL.revokeObjectURL(u)); cache.clear(); cacheLib = m.dirName; }
  const toggle = (k) => setSel((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const dragPaths = (a) => (sel.has(a.key) ? albums.filter((x) => sel.has(x.key)).flatMap((x) => x.paths) : a.paths);
  if (!albums.length) return <p className="py-12 text-center text-sm text-[#7A7468]" data-testid="insp-empty">No albums here yet.</p>;
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-6" data-testid="insp-album-grid">
      {albums.map((a, i) => <Tile key={a.key} m={m} album={a} i={i} selected={sel.has(a.key)} onToggle={() => toggle(a.key)} onOpen={() => onOpen(a.key)} dragPaths={dragPaths} />)}
    </div>
  );
};
